import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { monitor, METRIC_NAMES, PAGE_IDS } from 'src/utils/monitoring';
import { VALUE_MATCHING_BASE, SLUG_TO_CAMPAIGN_TYPE } from 'src/routes/valueMatching/routes';
import { MPCS_BASE } from 'src/routes/mpcs/routes';

/**
 * Derives a bounded page identifier from the route pathname. Kept bounded
 * (never the raw pathname) so the Page.View `page` dimension stays
 * low-cardinality even on routes that embed campaignId/version.
 */
const getPageId = (pathname: string): string => {
  if (pathname === VALUE_MATCHING_BASE) return PAGE_IDS.HOME;
  if (pathname.startsWith(MPCS_BASE)) return PAGE_IDS.COMPETITOR_PRICING;
  if (pathname.endsWith('/list')) return PAGE_IDS.LIST_CAMPAIGNS;
  if (pathname.endsWith('/create')) return PAGE_IDS.CREATE_CAMPAIGN;
  if (pathname.includes('/view/')) return PAGE_IDS.VIEW_CAMPAIGN;
  if (pathname.includes('/modify/')) return PAGE_IDS.MODIFY_CAMPAIGN;
  return PAGE_IDS.UNKNOWN;
};

/** Resolves the CampaignType enum from the :campaignType URL slug, when present. */
const getCampaignType = (pathname: string): string | undefined => {
  if (!pathname.startsWith(`${VALUE_MATCHING_BASE}/`)) return undefined;
  const slug = pathname.slice(VALUE_MATCHING_BASE.length + 1).split('/')[0];
  return SLUG_TO_CAMPAIGN_TYPE[slug];
};

/**
 * Emits one Page.View metric per route change — including direct-URL / bookmark
 * landings, since it fires on mount. Mounted in the layout shells so every route
 * is covered exactly once. Total site traffic = SUM(Page.View).
 */
export const usePageViewMetric = (): void => {
  const { pathname } = useLocation();
  useEffect(() => {
    const campaignType = getCampaignType(pathname);
    monitor.count(METRIC_NAMES.PAGE_VIEW, 1, {
      page: getPageId(pathname),
      methodName: 'PageView',
      ...(campaignType && { campaignType }),
    });
  }, [pathname]);
};
